// Words that read on whatever a slide stands on (S6). A slide's background (its own, or its theme's) can be a dark
// photo under a light theme's dark words, or a pale gradient under a dark theme's light ones: there the slide's inks
// turn to ones that read, as the Bold style's title slides do on the accent, taken from the theme itself (its
// background colour as the light or dark ink) where that reads. Every place a slide shows uses this, the PowerPoint
// file too, so a deck reads the same everywhere. A picture's tone is read once from its pixels, small.

import { useEffect, useMemo, useState } from "react";
import { colorValue, type Palette, type SlideBg } from "../../../../shared/slideDesign";
import { contrastOf, isDarkHex, mixHex } from "../../../../shared/deckTheme";
import { picturePath } from "../../../../shared/imageInfo";
import type { SlideData } from "../../../../shared/slides";
import { faceVars, slideColors, slidePalette, type DeckLook } from "../look";
import { useBgDraft } from "./draft";

// ---------------------------------------------------------------------------
// a picture's tone

const TONES = new Map<string, number>();
const waiting = new Map<string, Promise<number | null>>();

/** How light a kept picture is on average, from 0 (black) to 1 (white), read small from its pixels once. */
export function pictureTone(key: string): Promise<number | null> {
  if (TONES.has(key)) return Promise.resolve(TONES.get(key)!);
  if (waiting.has(key)) return waiting.get(key)!;
  const p = new Promise<number | null>((done) => {
    if (typeof Image === "undefined") return done(null);
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = 32;
        c.height = 18;
        const g = c.getContext("2d", { willReadFrequently: true });
        if (!g) return done(null);
        g.drawImage(img, 0, 0, 32, 18);
        const d = g.getImageData(0, 0, 32, 18).data;
        let sum = 0;
        for (let i = 0; i < d.length; i += 4) sum += (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
        const t = sum / (d.length / 4);
        TONES.set(key, t);
        done(t);
      } catch {
        done(null);
      }
    };
    img.onerror = () => done(null);
    img.src = picturePath(key);
  });
  waiting.set(key, p);
  return p;
}

export function usePictureTone(key: string | null): number | null {
  const [tone, setTone] = useState<number | null>(key ? (TONES.get(key) ?? null) : null);
  useEffect(() => {
    if (!key) return setTone(null);
    if (TONES.has(key)) return setTone(TONES.get(key)!);
    let live = true;
    void pictureTone(key).then((t) => live && setTone(t));
    return () => {
      live = false;
    };
  }, [key]);
  return tone;
}

/** The ground a picture with its veil reads as: its average tone, darkened or lightened by the veil. */
export function veiledGround(tone: number, veil: number): string {
  const g = Math.round(Math.max(0, Math.min(1, tone)) * 255);
  const base = "#" + [g, g, g].map((x) => x.toString(16).padStart(2, "0")).join("");
  return veil < 0 ? mixHex(base, "#000000", -veil) : veil > 0 ? mixHex(base, "#FFFFFF", veil) : base;
}

/**
 * The veil that makes words read on a picture, the nearest to the one it has; null when they read now. A picture's
 * average tone hides its light and dark parts, so its words ask 6:1 of the average rather than 4.5:1.
 */
export function veilToRead(tone: number, veil: number): number | null {
  const reads = (v: number) => {
    const g = veiledGround(tone, v);
    return Math.max(contrastOf("#FFFFFF", g), contrastOf("#111317", g)) >= 6;
  };
  if (reads(veil)) return null;
  for (let d = 0.05; d <= 1.8; d += 0.05) {
    const down = Math.round((veil - d) * 100) / 100;
    const up = Math.round((veil + d) * 100) / 100;
    if (down >= -0.9 && reads(down)) return down;
    if (up <= 0.9 && reads(up)) return up;
  }
  return null;
}

// ---------------------------------------------------------------------------
// the inks

export interface Ink {
  fg: string;
  fg2: string;
  fg3: string;
  line: string;
  accent: string;
  dark: boolean;
  /** what the words stand on, as one colour */
  ground: string;
}

/** What a background reads as, for words: a colour, a gradient's middle, a picture's tone under its veil; null when unknown. */
export function groundOf(bg: SlideBg, palette: Palette, tone: number | null): string | null {
  if (bg.kind === "color") return colorValue(bg.color, palette, palette.background);
  if (bg.kind === "gradient") return mixHex(colorValue(bg.from, palette, palette.background), colorValue(bg.to, palette, palette.background), 0.5);
  const veil = bg.veil ?? 0;
  if (tone !== null) return veiledGround(tone, veil);
  // not read yet: a strong veil says enough
  if (veil <= -0.3) return veiledGround(0.35, veil);
  if (veil >= 0.3) return veiledGround(0.65, veil);
  return null;
}

const rgba = (hex: string, a: number) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  return `rgba(${n[0]}, ${n[1]}, ${n[2]}, ${a})`;
};

/**
 * The inks a slide's words take on its background: null where the theme's own read (3:1 or better, as the board asks
 * of large type); else a light or a dark ink that does, the theme's own background colour where it reads, so the
 * slide stays in the theme's colours.
 */
export function inkFor(look: DeckLook, layout: SlideData["layout"], bg: SlideBg | null, tone: number | null): Ink | null {
  if (!bg) return null;
  const c = slideColors(look, layout);
  const palette = slidePalette(look, layout);
  const ground = groundOf(bg, palette, tone);
  if (!ground || contrastOf(c.fg.startsWith("#") ? c.fg : look.palette.text, ground) >= 3) return null;
  const darkGround = isDarkHex(ground) || contrastOf("#FFFFFF", ground) > contrastOf("#111317", ground);
  const own = look.palette.background;
  const fg = darkGround ? (contrastOf(own, ground) >= 4.5 && !isDarkHex(own) ? own : "#FFFFFF") : contrastOf(own, ground) >= 4.5 && isDarkHex(own) ? own : "#111317";
  return {
    fg,
    fg2: rgba(fg, 0.84),
    fg3: rgba(fg, 0.64),
    line: rgba(fg, 0.26),
    accent: contrastOf(look.accent, ground) >= 3 ? look.accent : fg,
    dark: darkGround,
    ground,
  };
}

/** A look with a slide's inks in place of the theme's, for what draws one slide outside a face (the PowerPoint file). */
export function lookWithInk(look: DeckLook, ink: Ink | null): DeckLook {
  if (!ink) return look;
  const solid = (c: string, a: number) => mixHex(c, ink.ground, 1 - a);
  return { ...look, fg: ink.fg, fg2: solid(ink.fg, 0.84), fg3: solid(ink.fg, 0.64), line: ink.line, accent: ink.accent, dark: ink.dark, keyOnAccent: false, palette: { ...look.palette, text: ink.fg, muted: solid(ink.fg, 0.84), accent: ink.accent } };
}

/**
 * What one slide is drawn with: its background (a background being dragged into shape in the editor, its own, else
 * its theme's; a slide standing on the accent keeps it), its face's variables and its theme colours, with inks that
 * read on that background.
 */
export function useSlideLook(look: DeckLook, s: SlideData, mode: string): { bg: SlideBg | null; vars: Record<string, string>; palette: Palette } {
  const draft = useBgDraft((d) => (mode === "edit" && d.on ? d : null));
  const keyed = look.keyOnAccent && (s.layout === "title" || s.layout === "section");
  const own = draft ? draft.bg : s.bg;
  const bg = own ?? (keyed ? null : (look.deckBg ?? null));
  const tone = usePictureTone(bg?.kind === "picture" ? bg.image.key : null);
  const bgKey = bg ? JSON.stringify(bg) : "";
  return useMemo(() => {
    const vars = faceVars(look, s.layout);
    const palette = slidePalette(look, s.layout);
    // a slide brought from a PowerPoint file keeps the file's own colours (S7)
    const ink = s.plain ? null : inkFor(look, s.layout, bg, tone);
    if (!ink) return { bg, vars, palette };
    return {
      bg,
      vars: { ...vars, "--sl-fg": ink.fg, "--sl-fg2": ink.fg2, "--sl-fg3": ink.fg3, "--sl-line": ink.line, "--sl-accent": ink.accent, "--fg": ink.fg, "--fg-2": ink.fg2, "--fg-3": ink.fg3, "--accent": ink.accent, colorScheme: ink.dark ? "dark" : "light" },
      palette: { ...palette, text: ink.fg, muted: mixHex(ink.fg, ink.ground, 0.2), accent: ink.accent },
    };
  }, [look, s.layout, s.plain, bgKey, tone]); // eslint-disable-line react-hooks/exhaustive-deps
}
